<template>
  <label :class="{ 'p-error': invalid, 'p-disabled': disabled }" v-if="!hideLabel">{{ label }}</label>
  <div
    class="tree-search"
    :ref="`treeSearchInput-${optionsPanelId}`"
    :class="[{ open: isOpen, disabled: disabled, large: size === 'large' }, `tree-search-${optionsPanelId}`]"
  >
    <div class="tree-search-input" :class="{ 'p-invalid': invalid }" @click="openResults">
      <div class="tree-search-value" :class="{ 'p-disabled': disabled }">
        <slot name="prepend"></slot>
        <template v-if="value">
          <div v-if="!isMulti && !isOpen" @click.stop="openResults" v-html="getLabel(value, stripTags, true)"></div>
          <template v-else-if="Array.isArray(value)">
            <DraggableComponent v-model="value" ghost-class="surface-500">
              <template #item="{ element, index }">
                <div class="tree-search-multi-val inline-block">
                  <span v-html="getLabel(element, stripTags)"></span>
                  <i class="fa fa-trash" @click.stop="clearValueItem(index)"/>
                </div>
              </template>
            </DraggableComponent>
          </template>
        </template>
        <input
          :ref="`textInput-${optionsPanelId}`"
          v-show="isOpen || !value || isMulti"
          type="text"
          v-model="filter"
          :placeholder="value && !isMulti ? getLabel(value, true, true) : $t(placeholder)"
          :disabled="disabled"
          @input="debounceInput"
          @keyup="onKeyUp"
          autocomplete="new-value"
          @blur="$emit('blur')"
          tabindex="0"
        />
        <slot name="append"></slot>
      </div>
      <div class="tree-search-icons">
        <i v-if="isLoading" class="fa fa-circle-o-notch fa-spin"/>
        <div v-if="value && hasClearValue && !disabled" @click.stop="clearValue">&times;</div>
        <div @click.stop="toggleOpen">
          <i v-if="!isOpen" class="fa fa-caret-down"/>
          <i v-else class="fa fa-caret-up"/>
        </div>
      </div>
    </div>
    <OverlayPanel
      @hide="isOpen = false"
      @show="isOpen = true"
      :ref="`optionsPanel-${optionsPanelId}`"
      id="optionsPanel"
      style="max-height: 300px; z-index: 999999"
      :style="{
        width: inputWidth,
        position: appendToSelf ? 'fixed' : '',
        display: appendToSelf ? 'block' : '',
        overflowY: isLoading ? 'hidden' : 'auto',
      }"
      :appendTo="appendToSelf ? 'self' : 'body'"
      @scroll="scroll"
    >
      <div v-if="showTreeSelectOptions && isMulti" class="gis-sortable-tree-options">
        <a href="#" @click.prevent="selectAll">{{ $t('Select all') }}</a>
        <a href="#" @click.prevent="clearValue" v-if="value?.length">{{ $t('Clear all') }}</a>
      </div>
      <SortableTree
        v-if="treeOptions.length"
        :data="treeOptions"
        :selected="value"
        :is-sortable="false"
        :item-key="itemKey"
        :item-parent-key="parentKey"
        :label-property="labelKey"
        :allowActions="[]"
        :as-tree-input="true"
        :strip-tags="stripTags"
        :disable-branch-nodes="disableBranchNodes"
        @select="onOptionClick"
        @openMenu="openContextMenu"
      />
      <div v-else>
        <small v-if="!async || filter" class="font-italic">{{ $t('No records.') }}</small>
        <small v-else class="font-italic">{{ $t('Search for records...') }}</small>
      </div>
      <!--Loading
        :active="isLoading"
        :is-full-page="false"
        style="position: absolute; top: 50%; transform: translateY(-50%)"
        loader="bars"
      /-->
      <Loader
        :is-loading="isLoading"
        :is-local-loader="true"
      />
    </OverlayPanel>
  </div>
  <template v-if="error && showErrors">
    <template v-for="errorObj in error">
      <small class="p-error">{{ errorObj.$message }}</small>
    </template>
  </template>
  <small v-if="createOnEnter" class="block font-italic">{{ $t('Hit ENTER key to create new value.') }}</small>
</template>

<script>
import SortableTree from '@/components/content/SortableTree.vue';
import {flatTree, getNestedList, normalizeString, stripHtml} from '@/js/utils';
import {filterNestedArray, getTreeItemValue} from '@/js/functions';
import {objectVariable} from '@/js/document/document';
import DraggableComponent from 'vuedraggable';
import Loading from 'vue3-loading-overlay';
import isEqual from 'lodash/isEqual';
import Loader from "@/components/common/Loader.vue";

export default {
  name: 'TreeInput',
  components: {Loader, SortableTree, DraggableComponent, Loading},
  props: {
    modelValue: String,
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Choose option...',
    },
    error: {
      default: null,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: function () {
        return [];
      },
    },
    // required if options are loaded async and modelType is "id"
    modelOptions: {
      type: Array,
      required: false,
    },
    labelKey: {
      type: String,
      default: 'title',
    },
    labelPattern: {
      type: String,
    },
    selectedLabelPattern: {
      type: String,
    },
    queryKey: {
      type: String,
    },
    itemKey: {
      type: String,
      default: 'value',
    },
    parentKey: {
      type: String,
    },
    isMulti: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    async: {
      type: Boolean,
      default: false,
    },
    // object || id
    modelType: {
      type: String,
      default: 'object',
    },
    // list of field to be reduced the object, works with modelType: object
    modelSchema: {
      type: Array,
    },
    // if options are not already a tree, we do it here, requires parentKey
    transformToTree: {
      type: Boolean,
      default: true,
    },
    disableBranchNodes: {
      type: Boolean,
      default: false,
    },
    createOnEnter: {
      type: Boolean,
      default: false,
    },
    // some data that will be sent back on emitted events
    extraData: {
      type: Object,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    showErrors: {
      type: Boolean,
      default: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    appendToSelf: {
      type: Boolean,
      default: false,
    },
    hasStaticOptions: {
      type: Boolean,
      default: true,
    },
    hasClearValue: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'normal',
    },
    isInForm: {
      type: Boolean,
      default: false,
    },
    showTreeSelectOptions: {
      type: Boolean,
      default: false,
    },
    stripTags: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'update:modelValue',
    'blur',
    'openList',
    'scrollHalf',
    'change',
    'optionClick',
    'optionRemove',
    'optionAdd',
    'filter',
  ],
  computed: {
    value: {
      get() {
        if (this.isMulti && typeof this.modelValue === 'string') {
          return [this.modelValue];
        } else {
          return this.modelValue || null;
        }
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    treeOptions() {
      if (this.filter) {
        return this.filteredOptions;
      } else if (!this.createOnEnter) {
        if (this.transformToTree && this.parentKey) {
          return getNestedList(this.options, this.itemKey, this.parentKey);
        } else {
          return this.options;
        }
      } else {
        return this.options;
      }
    },
  },
  data() {
    return {
      filter: '',
      filteredOptions: [],
      isOpen: false,
      debounce: null,
      inputWidth: 'auto',
      debounceRefresh: null,
      iterationCount: 0,
      oldOptionsLength: null,
      optionsPanelId: null,
      isScrollClick: false
    };
  },
  watch: {
    options() {
      this.filterOptions();
    },
    /*treeOptions(newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        //if (this.isOpen) {
          //this.$refs[`optionsPanel-${this.optionsPanelId}`].hide();
          //clearTimeout(this.debounceRefresh);
          //this.debounceRefresh = setTimeout(this.refreshPanel, 0);
        //}
        if (this.$refs[`optionsPanel-${this.optionsPanelId}`].container) {
          this.$refs[`optionsPanel-${this.optionsPanelId}`].alignOverlay();
        }
      }
    },*/
    isOpen(val) {
      if (val) {
        this.$nextTick(() => this.$refs[`textInput-${this.optionsPanelId}`].focus());
        if (!this.filter) this.$emit('openList');
      }else{
        this.filter = '';
      }
    },
  },
  created() {
    this.optionsPanelId = Math.floor(Math.random() * 10000);

    if (this.isMulti) {
      if (!Array.isArray(this.modelValue)) {
        try {
          const parsedValue = JSON.parse(this.modelValue);
          if (!Array.isArray(parsedValue)) this.value = [];
          else this.value = parsedValue;
        } catch (_) {
          this.value = [];
        }
      }
    }
  },
  mounted() {
    this.filterOptions();
    if (this.autofocus) this.openResults();
    if (this.hasStaticOptions && this.modelValue && this.isInForm && typeof this.options[0]?.value === 'string')
      this.value = String(this.modelValue);

    // close options panel when document is clicked
    document.addEventListener('mouseup', this.handleDocumentClick);
    document.addEventListener('mousedown', this.handleDocumentClickDown);
    document.addEventListener('keydown', this.handleDocumentKey);
  },
  beforeUnmount() {
    document.removeEventListener('mouseup', this.handleDocumentClick);
    document.removeEventListener('mousedown', this.handleDocumentClickDown);
    document.removeEventListener('keydown', this.handleDocumentKey);
  },
  methods: {
    openContextMenu(e, item) {
      this.$emit('openMenu', e, item);
    },
    handleDocumentKey(event) {
      if (event.keyCode == 9) {
        this.$refs[`optionsPanel-${this.optionsPanelId}`]?.hide();
      }
    },
    handleDocumentClickDown(event) {
      try {
        const rect = event.target.getBoundingClientRect();
        this.isScrollClick = false;
        const isVerticalScrollbar = event.target.scrollHeight > event.target.clientHeight;
        if (isVerticalScrollbar) {
          const scrollbarWidth = event.target.offsetWidth - event.target.clientWidth;
          if (event.clientX >= rect.right - scrollbarWidth && event.clientX <= rect.right) {
            this.isScrollClick = true;
          }
        }
      } catch (_) {
      }
    },
    handleDocumentClick(event) {
      if (!this.isScrollClick) {
        const classRegex = new RegExp(`tree-search-${this.optionsPanelId}|p-overlaypanel`);
        if (!classRegex.test(event.target?.offsetParent?.className)) {
          this.$refs[`optionsPanel-${this.optionsPanelId}`].hide();
        }
        for (let panel in this.$refs) {
          if (/optionsPanel-/.test(panel) && panel !== `optionsPanel-${this.optionsPanelId}`) {
            this.$refs[panel].hide();
          }
        }
      }
    },
    resetIteration() {
      this.iterationCount = 0;
      this.oldOptionsLength = null;
    },
    scroll(ev) {
      if (
        ev.target.scrollTop > ev.target.scrollHeight - 500 &&
        this.treeOptions.length % 50 === 0 &&
        this.oldOptionsLength !== this.treeOptions.length
      ) {
        this.oldOptionsLength = this.treeOptions.length;
        this.iterationCount++;
        this.$emit('scrollHalf', normalizeString(this.filter), this.iterationCount);
      }
    },
    /*refreshPanel() {
      setTimeout(() => {
        this.$refs[`optionsPanel-${this.optionsPanelId}`].show({
          currentTarget: this.$refs[`treeSearchInput-${this.optionsPanelId}`],
        });
      }, 100);
    },*/
    filterOptions() {
      this.filteredOptions =
        this.filter && !this.async /* && this.hasStaticOptions*/
          ? filterNestedArray(
            JSON.parse(JSON.stringify(this.options)),
            normalizeString(this.filter),
            'children',
            this.queryKey || this.labelKey,
            false,
            []
          )
          : this.options;
    },
    onOptionClick(opt, isChild = false) {
      if (this.disableBranchNodes && opt.hasOwnProperty('children') && !isChild) {
        return;
      }
      const parsedValue = this.parseOption(opt);

      if (parsedValue) {
        if (!this.isMulti) {
          this.value = parsedValue;
          this.$refs[`optionsPanel-${this.optionsPanelId}`].hide();
          this.$emit('optionClick', parsedValue);
        } else {
          let newValue = this.value ? JSON.parse(JSON.stringify(this.value)) : [];

          const exists = newValue.find((v) => isEqual(v, parsedValue));
          if (exists) {
            this.$emit('optionClick', parsedValue);
            this.$emit('optionRemove', parsedValue);
            newValue = newValue.filter((v) => !isEqual(v, parsedValue));
          } else {
            this.$emit('optionClick', parsedValue);
            this.$emit('optionAdd', parsedValue);
            newValue.push(parsedValue);
          }
          this.value = newValue;
          this.filter = '';
          this.$nextTick(() => this.$refs[`textInput-${this.optionsPanelId}`].focus());
        }
        this.$emit('change', this.extraData);
      }
    },
    parseOption(opt) {
      if (!opt) return null;
      let value = JSON.parse(JSON.stringify(opt));
      if (this.modelType !== 'object') {
        value = /\./.test(this.itemKey) ? objectVariable(value, this.itemKey) : (typeof value === 'object'? value[this.itemKey]: value);
      } else if (this.modelSchema && this.modelSchema.length) {
        for (let key in opt) {
          if (this.modelSchema.indexOf(key) === -1) {
            delete value[key];
          }
        }
      }
      return value;
    },
    clearValue() {
      this.value = this.isMulti ? [] : null;
      this.filter = '';
      this.$refs[`optionsPanel-${this.optionsPanelId}`].hide();
      this.triggerFilter();
      this.$emit('change', this.extraData);
    },
    clearValueItem(index) {
      const removedItem = this.value.find((e, i) => i === index);
      this.$emit('optionRemove', removedItem);
      this.value = this.value.filter((e, i) => i !== index);
      this.$emit('change', this.extraData);
    },
    getLabel(value, forceStripTags = false, isSelected = false) {
      const opts = this.async && this.modelType === 'id' && this.modelOptions ? this.modelOptions : this.options;

      if (this.modelType === 'object') {
        let parsedLabelPattern;
        if (this.selectedLabelPattern && isSelected && /\$\{/.test(this.selectedLabelPattern)) {
          parsedLabelPattern = this.selectedLabelPattern?.interpolateObject(value);
        } else if (/\$\{/.test(this.labelPattern)) parsedLabelPattern = this.labelPattern?.interpolateObject(value);
        return (
          this.stripHtml(parsedLabelPattern, forceStripTags) ||
          this.stripHtml(value[this.labelKey], forceStripTags) ||
          this.stripHtml(this.labelKey?.interpolateObject(value), forceStripTags) ||
          this.stripHtml(value[this.itemKey], forceStripTags) ||
          this.stripHtml(value, forceStripTags)
        );
      } else {
        if (value[this.labelKey]) {
          return this.stripHtml(value[this.labelKey], forceStripTags);
        } else {
          let val = getTreeItemValue(opts, this.itemKey, value, 'children');
          return val
            ? this.stripHtml(val[this.labelKey], forceStripTags) ||
            this.stripHtml(this.labelKey?.interpolateObject(val), forceStripTags) ||
            this.stripHtml(val[this.itemKey], forceStripTags) ||
            this.stripHtml(val, forceStripTags)
            : this.stripHtml(value, forceStripTags);
        }
      }
    },
    debounceInput() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.triggerFilter();
      }, 600);
    },
    triggerFilter() {
      this.iterationCount = 0;
      this.oldOptionsLength = null;
      this.$emit('filter', normalizeString(this.filter), this.extraData);
      this.filterOptions();
    },
    openResults() {
      if (this.disabled) return;
      this.filterOptions();
      this.inputWidth = `${this.$refs[`treeSearchInput-${this.optionsPanelId}`].getBoundingClientRect().width}px`;
      this.$refs[`optionsPanel-${this.optionsPanelId}`].show({
        currentTarget: this.$refs[`treeSearchInput-${this.optionsPanelId}`],
      });
      this.resetIteration();
    },
    toggleOpen() {
      if (this.disabled) return;
      if (this.isOpen) this.$refs[`optionsPanel-${this.optionsPanelId}`].hide();
      if (!this.isOpen)
        this.$refs[`optionsPanel-${this.optionsPanelId}`].show({
          currentTarget: this.$refs[`treeSearchInput-${this.optionsPanelId}`],
        });
    },
    onKeyUp(e) {
      if (!this.isOpen) {
        this.toggleOpen();
      }
      if (this.createOnEnter && this.isMulti && (e.keyCode === 13 || e.which === 13)) {
        this.value.push(this.sanitize(this.filter));
        this.filter = '';
        this.$emit('change', this.extraData);
      }
    },
    stripHtml(html, stripTags = false) {
      if (typeof html === 'object') return '';
      return stripTags ? stripHtml(html) : html;
    },
    selectAll() {
      if (!this.isMulti) return;

      let value = [];
      let greenLight = true;
      const options = flatTree(JSON.parse(JSON.stringify(this.treeOptions)), []);

      if (options.length > 50 && !confirm(this.$t('There are more than 50 options to select. Are you sure?'))) {
        greenLight = false;
      }

      if (greenLight) {
        for (let opt of options) {
          value.push(this.parseOption(opt));
        }
        this.value = value;
      }
    },
    sanitize(value) {
      return this.$purify.sanitize(value);
    },
  },
};
</script>

<style scoped lang="scss">
.gis-sortable-tree-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: smaller;
  border-bottom: 1px solid #eee;
  padding-bottom: 5px;
  margin-bottom: 5px;
}

.p-invalid {
  border-color: var(--error-color) !important;
}

.tree-search {
  position: relative;
  width: 100%;
  min-width: 150px;
  max-width: 100%;

  .tree-search-input {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #ddd;
    border-radius: 5px;
    padding: 5px 10px;
    cursor: pointer;
    min-height: 36px;
    transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;

    .tree-search-value {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      flex: 1 0 auto;
      max-width: 90%;

      input {
        background: transparent;
        border: none;
        flex: 1 0 auto;
        min-height: 24px;
        outline: none;

        &::placeholder {
          color: lightgray;
        }
      }
    }
  }

  .tree-search-icons {
    display: flex;
    align-items: center;
    color: #ddd;
    margin-left: 10px;

    > * {
      margin-right: 5px;
    }
  }

  .tree-search-multi-val {
    margin: 2px;
    background: #0f8bfd;
    color: white;
    font-size: smaller;
    padding: 2px 5px;
    border-radius: 2px;
    white-space: normal;
  }

  &.open {
    .tree-search-input {
      border-color: #0f8bfd;
      //border-bottom-left-radius: 0;
      //border-bottom-right-radius: 0;
    }
  }

  &.disabled {
    cursor: not-allowed;

    .tree-search-input {
      cursor: not-allowed;
    }
  }

  &.large {
    .tree-search-input {
      min-height: 50px;
      font-size: larger;
      font-weight: bold;
    }
  }
}

input {
  color: var(--text-color);
}
</style>
